import React, { useState, useEffect, useRef } from "react";
import { FiSettings, FiDollarSign, FiMail } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { tenantDataService } from '../../services/tenantDataService';
import { tenantStorageService } from '../../services/tenantStorageService';
import tenantSlugService from '../../services/tenantSlugService';
import AdminCompensationSettings from './compensation/AdminCompensationSettings';
import ClinicEmailSettings from '../settings/ClinicEmailSettings';
import { getCurrencyOptions, getCurrencySymbol } from '../../utils/currency';
import { getUserTimezone } from '../../utils/timezone';
import TimezoneSelector from '../common/TimezoneSelector';
import toast from 'react-hot-toast';

const defaultSettings = {
  clinicName: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
  openingHours: "Mon–Fri 08:00–18:00",
  tenantSlug: "",
  businessHours: {
    monday: { open: '08:00', close: '18:00', closed: false },
    tuesday: { open: '08:00', close: '18:00', closed: false },
    wednesday: { open: '08:00', close: '18:00', closed: false },
    thursday: { open: '08:00', close: '18:00', closed: false },
    friday: { open: '08:00', close: '18:00', closed: false },
    saturday: { open: '09:00', close: '13:00', closed: true },
    sunday: { open: '09:00', close: '17:00', closed: true }
  },
  timezone: "Asia/Singapore",
  logo: "",
  websiteUrl: "",
  // Financial Settings
  treatmentRates: {
    "Physiotherapy": { type: "hourly", value: 150 },
    "Massage Therapy": { type: "hourly", value: 120 },
    "Consultation": { type: "flat", value: 80 },
    "Follow-up": { type: "flat", value: 60 }
  },
  roleRates: {
    "clinician": 60,
    "staff": 50,
    "admin": 70
  },
  vatPercentage: 20,
  currency: "GBP", // Default currency for invoices
  bankDetails: {
    accountName: "",
    accountNumber: "",
    sortCode: ""
  },
  startingInvoiceNumber: "INV-0000001",
  // Invoice Legal Compliance Settings
  invoiceCompliance: {
    paymentTerms: {
      enabled: true,
      dueDays: 30,
      latePaymentRate: 1.5
    },
    businessRegistration: {
      enabled: false,
      number: ""
    },
    vatTaxId: {
      enabled: false,
      number: ""
    },
    contactDispute: {
      enabled: true,
      email: "",
      phone: ""
    },
    paymentMethods: {
      enabled: true,
      methods: "Bank transfer, Card payments"
    }
  },
  // Leave Management Settings
  leaveSettings: {
    yearStartMonth: 1, // January (1-12)
    yearStartDay: 1,   // 1st day of month
    autoRollover: true, // Automatically rollover to next year
    annualLeaveAllowance: 25, // Days per year
    sickLeaveAllowance: 10,   // Days per year
    personalLeaveAllowance: 5, // Days per year
    maternityLeaveAllowance: 90, // Days per year
    emergencyLeaveAllowance: 3   // Days per year
  }
};

// Tenant-scoped settings functions
const getStoredSettings = async (tenantId) => {
  if (!tenantId) return null;
  try {
    tenantDataService.setTenantContext(tenantId);
    const settingsDoc = await tenantDataService.getById('settings', 'clinic_settings');
    return settingsDoc || null;
  } catch (error) {
    console.error('Error loading tenant settings:', error);
    return null;
  }
};

const saveStoredSettings = async (tenantId, settings) => {
  if (!tenantId) throw new Error('Tenant ID required');
  try {
    tenantDataService.setTenantContext(tenantId);
    // Try to update first, if it fails, create new
    try {
      await tenantDataService.update('settings', 'clinic_settings', settings);
    } catch (updateError) {
      // If update fails (document doesn't exist), create it
      await tenantDataService.create('settings', { ...settings, id: 'clinic_settings' });
    }
    return true;
  } catch (error) {
    console.error('Error saving tenant settings:', error);
    return false;
  }
};

// Helper function to get current leave year display
const getCurrentLeaveYear = (leaveSettings = {}) => {
  const currentDate = new Date();
  const startMonth = (leaveSettings.yearStartMonth || 1) - 1; // 0-indexed
  const startDay = leaveSettings.yearStartDay || 1;

  const currentYear = currentDate.getFullYear();
  const leaveYearStart = new Date(currentYear, startMonth, startDay);

  if (currentDate >= leaveYearStart) {
    // Current leave year
    const nextYearStart = new Date(currentYear + 1, startMonth, startDay - 1);
    return `${leaveYearStart.toLocaleDateString()} - ${nextYearStart.toLocaleDateString()}`;
  } else {
    // Previous leave year
    const prevYearStart = new Date(currentYear - 1, startMonth, startDay);
    const currentYearEnd = new Date(currentYear, startMonth, startDay - 1);
    return `${prevYearStart.toLocaleDateString()} - ${currentYearEnd.toLocaleDateString()}`;
  }
};

export default function AdminSettings() {
  const { currentTenant } = useTenant();
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const fileInputRef = useRef(null);
  const [slugValidating, setSlugValidating] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!currentTenant?.id) return;

      setLoading(true);
      try {
        console.log('🔍 AdminSettings - Loading settings for tenant:', currentTenant.id);
        const storedSettings = await getStoredSettings(currentTenant.id);
        console.log('🔍 AdminSettings - Loaded stored settings:', storedSettings);
        console.log('🔍 AdminSettings - Stored treatmentRates:', storedSettings?.treatmentRates);

        // Load existing tenant slug if available
        let tenantSlug = '';
        try {
          const existingSlug = await tenantSlugService.getSlugByTenantId(currentTenant.id);
          if (existingSlug) {
            tenantSlug = existingSlug;
          }
        } catch (error) {
          console.log('No existing slug found for tenant');
        }

        if (storedSettings) {
          setSettings({ ...defaultSettings, ...storedSettings, tenantSlug });
        } else {
          console.log('🔍 AdminSettings - No stored settings found, using defaults');
          setSettings({ ...defaultSettings, tenantSlug });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [currentTenant?.id]);

  function handleChange(e) {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  }

  // Validate slug availability
  const validateSlug = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }

    setSlugValidating(true);
    try {
      // First check if this is the current tenant's existing slug
      const existingSlug = await tenantSlugService.getSlugByTenantId(currentTenant.id);

      if (existingSlug === slug) {
        // This is their current slug, so it's available for them
        setSlugAvailable(true);
      } else {
        // Check if slug is available for new use
        const isAvailable = await tenantSlugService.isSlugAvailable(slug);
        setSlugAvailable(isAvailable);
      }
    } catch (error) {
      console.error('Error validating slug:', error);
      // If there's an error, assume it's available (could be network issue)
      setSlugAvailable(true);
    } finally {
      setSlugValidating(false);
    }
  };

  // Handle slug change with validation
  const handleSlugChange = (e) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSettings(prev => ({ ...prev, tenantSlug: slug }));

    // Debounce validation
    setTimeout(() => validateSlug(slug), 500);
  };

  // Auto-generate opening hours text from detailed business hours
  const generateOpeningHoursText = (businessHours) => {
    if (!businessHours) return "Not set";

    const openDays = Object.entries(businessHours)
      .filter(([day, hours]) => !hours.closed)
      .map(([day, hours]) => ({ day, ...hours }));

    if (openDays.length === 0) return "Closed";

    // Group consecutive days with same hours
    const groups = [];
    let currentGroup = null;

    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayAbbr = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

    dayOrder.forEach(day => {
      const dayData = openDays.find(d => d.day === day);
      if (dayData) {
        const timeRange = `${dayData.open}–${dayData.close}`;
        if (currentGroup && currentGroup.timeRange === timeRange) {
          currentGroup.endDay = day;
        } else {
          if (currentGroup) groups.push(currentGroup);
          currentGroup = { startDay: day, endDay: day, timeRange };
        }
      } else {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = null;
      }
    });
    if (currentGroup) groups.push(currentGroup);

    return groups.map(group => {
      const start = dayAbbr[group.startDay];
      const end = dayAbbr[group.endDay];
      const days = start === end ? start : `${start}–${end}`;
      return `${days} ${group.timeRange}`;
    }).join(', ');
  };

  // Update opening hours text when business hours change
  useEffect(() => {
    if (settings.businessHours) {
      const generatedText = generateOpeningHoursText(settings.businessHours);
      setSettings(prev => ({ ...prev, openingHours: generatedText }));
    }
  }, [settings.businessHours]);

  const handleSave = async (e) => {
    e.preventDefault();

    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    // Validate slug before saving
    if (settings.tenantSlug && slugAvailable === false) {
      toast.error('Please choose an available slug before saving');
      return;
    }

    setSaving(true);
    try {
      console.log('🔍 AdminSettings - Saving settings:', settings);
      console.log('🔍 AdminSettings - Treatment rates being saved:', settings.treatmentRates);

      // Save clinic settings
      await saveStoredSettings(currentTenant.id, settings);

      // Create or update tenant slug if provided
      if (settings.tenantSlug) {
        try {
          await tenantSlugService.createOrUpdateSlug(settings.tenantSlug, {
            tenantId: currentTenant.id,
            active: true,
            name: settings.clinicName || currentTenant.name,
            clinicName: settings.clinicName || currentTenant.name,
            timezone: settings.timezone || 'Europe/London'
          });
          toast.success('Tenant slug created successfully');
        } catch (slugError) {
          console.error('Error creating tenant slug:', slugError);
          toast.error('Settings saved but failed to create booking URL slug');
        }
      }

      setEditing(false);
      setMessage("Settings saved!");
      toast.success('Clinic settings saved successfully');

      // Dispatch custom event for sidebar updates
      window.dispatchEvent(new Event("clinicflow-settings-updated"));

      setTimeout(() => setMessage(""), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save clinic settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    // Check if user is authenticated
    if (!currentUser || !currentUser.uid) {
      console.log('User auth state:', { currentUser, hasUid: !!currentUser?.uid });
      toast.error('You must be logged in to upload files');
      return;
    }

    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Logo file size must be less than 5MB');
      return;
    }

    try {
      // Use base64 approach like client documents to bypass CORS
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          // Update settings with base64 data
          setSettings(prev => ({
            ...prev,
            logo: reader.result, // Store as base64 data URL
            logoName: file.name,
            logoSize: file.size,
            logoType: file.type,
            logoUploadedAt: new Date().toISOString(),
            logoUploadedBy: currentUser.displayName || currentUser.email || 'Unknown User'
          }));

          toast.success('Logo uploaded successfully');
        } catch (updateError) {
          console.error('Error updating logo settings:', updateError);
          toast.error('Failed to save logo');
        }
      };

      reader.onerror = () => {
        toast.error('Failed to read logo file');
      };

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(`Upload failed: ${error.message}`);
    }
  };

  const handleLogoRemove = async () => {
    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    try {
      // Delete from storage if logoPath exists
      if (settings.logoPath) {
        tenantStorageService.setTenantContext(currentTenant.id);
        await tenantStorageService.deleteFile(settings.logoPath);
      }

      setSettings(prev => ({ ...prev, logo: "", logoPath: "" }));
      if (fileInputRef.current) fileInputRef.current.value = "";

      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
      // Still remove from UI even if storage deletion fails
      setSettings(prev => ({ ...prev, logo: "", logoPath: "" }));
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading clinic settings...</div>
      </div>
    );
  }

  if (!currentTenant) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">No clinic selected</div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Clinic Settings</h2>
      <div className="text-sm text-gray-600 mb-4">
        Managing settings for: <strong>{currentTenant.name}</strong>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'general'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FiSettings className="inline mr-2" size={16} />
            General Settings
          </button>
          <button
            onClick={() => setActiveTab('compensation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'compensation'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FiDollarSign className="inline mr-2" size={16} />
            Compensation Plans
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'email'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FiMail className="inline mr-2" size={16} />
            Email Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'compensation' ? (
        <AdminCompensationSettings />
      ) : activeTab === 'email' ? (
        <ClinicEmailSettings />
      ) : (
        <div>
          {!editing ? (
            <div className="space-y-3 mb-6">
              {settings.logo && (
                <div className="mb-2">
                  <img
                    src={settings.logo}
                    alt="Clinic logo"
                    style={{
                      maxHeight: 80,
                      maxWidth: 240,
                      objectFit: "contain",
                      borderRadius: 8,
                      background: "#fff",
                      border: "1px solid #e5e7eb"
                    }}
                    className="shadow"
                  />
                </div>
              )}
              <div>
                <strong>Name:</strong>{" "}
                {settings.clinicName || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Contact Email:</strong>{" "}
                {settings.contactEmail || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Contact Phone:</strong>{" "}
                {settings.contactPhone || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Address:</strong>{" "}
                {settings.address || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Opening Hours:</strong>{" "}
                {settings.openingHours || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Timezone:</strong>{" "}
                {settings.timezone || <span className="text-gray-400">Not set</span>}
              </div>
              <div>
                <strong>Booking URL Slug:</strong>{" "}
                {settings.tenantSlug ? (
                  <span className="text-green-600 font-mono">{settings.tenantSlug}</span>
                ) : (
                  <span className="text-gray-400">Not set</span>
                )}
              </div>
              {settings.tenantSlug && (
                <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                  <strong>Public Booking URL:</strong>{" "}
                  <code className="bg-white px-2 py-1 rounded border">
                    https://healehub.com/book/{settings.tenantSlug}
                  </code>
                </div>
              )}
              {settings.websiteUrl && (
                <div>
                  <strong>Website:</strong>{" "}
                  <a
                    href={settings.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 underline"
                  >
                    {settings.websiteUrl}
                  </a>
                </div>
              )}
              <button
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => setEditing(true)}
              >
                Edit Settings
              </button>
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-4 bg-gray-50 p-4 rounded shadow max-w-lg">
              <div>
                <label className="block font-semibold mb-1">Clinic Logo</label>
                {settings.logo && (
                  <div className="mb-2 flex items-center gap-2">
                    <img
                      src={settings.logo}
                      alt="Clinic logo"
                      style={{
                        maxHeight: 60,
                        maxWidth: 180,
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: "#fff"
                      }}
                      className="shadow"
                    />
                    <button
                      type="button"
                      onClick={handleLogoRemove}
                      className="ml-2 px-2 py-1 bg-red-200 text-red-800 rounded hover:bg-red-400 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="block mt-2 text-sm"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Upload a logo for your clinic (PNG/JPG, recommended max width 240px).
                </div>
              </div>
              <div>
                <label className="block font-semibold mb-1">Clinic Name</label>
                <input
                  type="text"
                  name="clinicName"
                  value={settings.clinicName}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={settings.contactEmail}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Contact Phone</label>
                <input
                  type="text"
                  name="contactPhone"
                  value={settings.contactPhone}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={settings.address}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Opening Hours</label>
                <div className="w-full border rounded px-2 py-1 mb-3 bg-gray-100 text-gray-700">
                  {settings.openingHours || "Not set"}
                </div>
                <div className="text-xs text-gray-500 mb-3">
                  This is automatically generated from your detailed business hours below.
                </div>

                {/* Detailed Business Hours */}
                <div className="mt-4 p-4 border rounded bg-gray-50">
                  <h4 className="font-semibold mb-3">Business Hours</h4>
                  <div className="space-y-3">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                      const hours = settings.businessHours?.[day] || { open: '08:00', close: '18:00', closed: false };
                      return (
                        <div key={day} className="flex items-center space-x-3">
                          <div className="w-20 text-sm font-medium capitalize">{day}</div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={!hours.closed}
                              onChange={(e) => {
                                const newBusinessHours = { ...settings.businessHours };
                                newBusinessHours[day] = { ...hours, closed: !e.target.checked };
                                setSettings(prev => ({ ...prev, businessHours: newBusinessHours }));
                              }}
                              className="mr-2"
                            />
                            Open
                          </label>
                          {!hours.closed && (
                            <>
                              <input
                                type="time"
                                value={hours.open}
                                onChange={(e) => {
                                  const newBusinessHours = { ...settings.businessHours };
                                  newBusinessHours[day] = { ...hours, open: e.target.value };
                                  setSettings(prev => ({ ...prev, businessHours: newBusinessHours }));
                                }}
                                className="border rounded px-2 py-1 text-sm"
                              />
                              <span className="text-sm">to</span>
                              <input
                                type="time"
                                value={hours.close}
                                onChange={(e) => {
                                  const newBusinessHours = { ...settings.businessHours };
                                  newBusinessHours[day] = { ...hours, close: e.target.value };
                                  setSettings(prev => ({ ...prev, businessHours: newBusinessHours }));
                                }}
                                className="border rounded px-2 py-1 text-sm"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div>
                <label className="block font-semibold mb-1">Timezone</label>
                <TimezoneSelector
                  value={settings.timezone}
                  onChange={(value) => setSettings(prev => ({ ...prev, timezone: value }))}
                  className="mb-2"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Your current system timezone: <strong>{getUserTimezone()}</strong>
                  {settings.timezone !== getUserTimezone() && (
                    <span className="text-amber-600 ml-2">
                      ⚠️ Different from system timezone
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block font-semibold mb-1">Practice Website URL</label>
                <input
                  type="url"
                  name="websiteUrl"
                  value={settings.websiteUrl || ""}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1"
                  placeholder="https://yourclinic.com"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Add your clinic's website. Logo in the sidebar will link here.
                </div>
              </div>

              {/* Public Booking Settings */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Public Booking Settings</h3>

                <div>
                  <label className="block font-semibold mb-1">Booking URL Slug</label>
                  <input
                    type="text"
                    value={settings.tenantSlug || ""}
                    onChange={handleSlugChange}
                    className={`w-full border rounded px-2 py-1 ${slugAvailable === false ? 'border-red-500' :
                      slugAvailable === true ? 'border-green-500' : ''
                      }`}
                    placeholder="your-clinic-name"
                    pattern="[a-z0-9-]+"
                    title="Only lowercase letters, numbers, and hyphens allowed"
                  />

                  {/* Slug validation feedback */}
                  <div className="mt-2 text-sm">
                    {slugValidating && (
                      <span className="text-blue-600">⏳ Checking availability...</span>
                    )}
                    {!slugValidating && slugAvailable === true && settings.tenantSlug && (
                      <span className="text-green-600">✅ Available</span>
                    )}
                    {!slugValidating && slugAvailable === false && (
                      <span className="text-red-600">❌ This slug is already taken</span>
                    )}
                    {settings.tenantSlug && settings.tenantSlug.length < 3 && (
                      <span className="text-orange-600">⚠️ Slug must be at least 3 characters</span>
                    )}
                  </div>

                  {/* Public URL preview */}
                  {settings.tenantSlug && slugAvailable !== false && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm font-medium text-blue-900 mb-1">
                        Your Public Booking URL:
                      </div>
                      <code className="text-sm bg-white px-2 py-1 rounded border text-blue-700">
                        https://healehub.com/book/{settings.tenantSlug}
                      </code>
                      <div className="text-xs text-blue-700 mt-1">
                        Patients can use this URL to book appointments directly
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mt-2">
                    Create a unique URL slug for your public booking page. Only lowercase letters, numbers, and hyphens are allowed.
                    <br />
                    Example: "heale-rehab" creates the URL "/book/heale-rehab"
                  </div>
                </div>
              </div>

              {/* Financial Settings Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Settings</h3>

                {/* Treatment Rates */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Treatment Rates</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newRates = { ...settings.treatmentRates };
                        const newTreatmentName = `New Treatment ${Object.keys(newRates).length + 1}`;
                        newRates[newTreatmentName] = { type: "hourly", value: 0, onlineBooking: true };
                        setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                      }}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      + Add Treatment
                    </button>
                  </div>

                  {/* Header Row */}
                  <div className="grid grid-cols-5 gap-3 items-center mb-2 text-sm font-medium text-gray-700 border-b pb-2">
                    <div>Treatment Name</div>
                    <div>Billing Type</div>
                    <div>Rate</div>
                    <div>Online Booking</div>
                    <div>Actions</div>
                  </div>

                  <div className="space-y-3">
                    {Object.entries(settings.treatmentRates || {}).map(([treatment, rate], index) => (
                      <div key={`treatment-${index}`} className="grid grid-cols-5 gap-3 items-center">
                        <div>
                          <input
                            type="text"
                            defaultValue={treatment}
                            onBlur={(e) => {
                              const newValue = e.target.value;
                              if (newValue !== treatment) {
                                const newRates = { ...settings.treatmentRates };
                                delete newRates[treatment];
                                newRates[newValue] = rate;
                                setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                              }
                            }}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="Treatment name"
                          />
                        </div>
                        <div>
                          <select
                            value={rate.type}
                            onChange={(e) => {
                              const newRates = { ...settings.treatmentRates };
                              newRates[treatment] = { ...rate, type: e.target.value };
                              setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                            }}
                            className="w-full border rounded px-2 py-1 text-sm"
                          >
                            <option value="hourly">Hourly</option>
                            <option value="flat">Flat Rate</option>
                          </select>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm">{getCurrencySymbol(settings.currency)}</span>
                          <input
                            type="number"
                            value={rate.value}
                            onChange={(e) => {
                              const newRates = { ...settings.treatmentRates };
                              newRates[treatment] = { ...rate, value: parseFloat(e.target.value) || 0 };
                              setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                            }}
                            className="w-full border rounded px-2 py-1 text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="flex items-center justify-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rate.onlineBooking !== false}
                              onChange={(e) => {
                                const newRates = { ...settings.treatmentRates };
                                newRates[treatment] = { ...rate, onlineBooking: e.target.checked };
                                setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">Allow</span>
                          </label>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              const newRates = { ...settings.treatmentRates };
                              delete newRates[treatment];
                              setSettings(prev => ({ ...prev, treatmentRates: newRates }));
                            }}
                            className="px-2 py-1 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors"
                            title="Remove treatment"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Add as many treatment types as your clinic offers. These rates will be used for invoice generation.
                    <br />
                    <strong>Online Booking:</strong> Check "Allow" for services that patients can book online through your public booking page.
                  </div>
                </div>

                {/* Role Rates */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Staff Role Rates (%)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(settings.roleRates || {}).map(([role, percentage]) => (
                      <div key={role}>
                        <label className="block text-sm font-medium mb-1 capitalize">{role.replace('_', ' ')}</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={percentage}
                            onChange={(e) => {
                              const newRates = { ...settings.roleRates };
                              newRates[role] = parseFloat(e.target.value) || 0;
                              setSettings(prev => ({ ...prev, roleRates: newRates }));
                            }}
                            className="w-full border rounded px-2 py-1 text-sm"
                            min="0"
                            max="100"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Currency Setting */}
                <div className="mb-6">
                  <label className="block font-semibold mb-1">Default Currency</label>
                  <select
                    value={settings.currency || 'GBP'}
                    onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-64 border rounded px-2 py-1"
                  >
                    {getCurrencyOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    This currency will be used as default for all new invoices. Can be overridden per invoice.
                  </div>
                </div>

                {/* VAT/Tax */}
                <div className="mb-6">
                  <label className="block font-semibold mb-1">VAT/Tax Percentage (%)</label>
                  <input
                    type="number"
                    value={settings.vatPercentage || 0}
                    onChange={(e) => setSettings(prev => ({ ...prev, vatPercentage: parseFloat(e.target.value) || 0 }))}
                    className="w-32 border rounded px-2 py-1"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>

                {/* Bank Details */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Bank Account Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Account Name</label>
                      <input
                        type="text"
                        value={settings.bankDetails?.accountName || ""}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          bankDetails: { ...prev.bankDetails, accountName: e.target.value }
                        }))}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Account Number</label>
                      <input
                        type="text"
                        value={settings.bankDetails?.accountNumber || ""}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          bankDetails: { ...prev.bankDetails, accountNumber: e.target.value }
                        }))}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Sort Code</label>
                      <input
                        type="text"
                        value={settings.bankDetails?.sortCode || ""}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          bankDetails: { ...prev.bankDetails, sortCode: e.target.value }
                        }))}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Starting Invoice Number */}
                <div className="mb-6">
                  <label className="block font-semibold mb-1">Starting Invoice Number</label>
                  <input
                    type="text"
                    value={settings.startingInvoiceNumber || "INV-0000001"}
                    onChange={(e) => setSettings(prev => ({ ...prev, startingInvoiceNumber: e.target.value }))}
                    className="w-48 border rounded px-2 py-1"
                    placeholder="INV-0000001"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Format: INV-0000001 (will auto-increment)
                  </div>
                </div>

                {/* Invoice Legal Compliance Settings */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold mb-4">Invoice Legal Compliance</h4>

                  {/* Payment Terms */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="paymentTermsEnabled"
                        checked={settings.invoiceCompliance?.paymentTerms?.enabled || false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          invoiceCompliance: {
                            ...prev.invoiceCompliance,
                            paymentTerms: { ...prev.invoiceCompliance?.paymentTerms, enabled: e.target.checked }
                          }
                        }))}
                        className="mr-2"
                      />
                      <label htmlFor="paymentTermsEnabled" className="font-medium">Payment Terms</label>
                    </div>
                    {settings.invoiceCompliance?.paymentTerms?.enabled && (
                      <div className="ml-6 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Due Days</label>
                          <input
                            type="number"
                            value={settings.invoiceCompliance?.paymentTerms?.dueDays || 30}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              invoiceCompliance: {
                                ...prev.invoiceCompliance,
                                paymentTerms: { ...prev.invoiceCompliance?.paymentTerms, dueDays: parseInt(e.target.value) }
                              }
                            }))}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Late Payment Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={settings.invoiceCompliance?.paymentTerms?.latePaymentRate || 1.5}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              invoiceCompliance: {
                                ...prev.invoiceCompliance,
                                paymentTerms: { ...prev.invoiceCompliance?.paymentTerms, latePaymentRate: parseFloat(e.target.value) }
                              }
                            }))}
                            className="w-full border rounded px-2 py-1 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Business Registration */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="businessRegEnabled"
                        checked={settings.invoiceCompliance?.businessRegistration?.enabled || false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          invoiceCompliance: {
                            ...prev.invoiceCompliance,
                            businessRegistration: { ...prev.invoiceCompliance?.businessRegistration, enabled: e.target.checked }
                          }
                        }))}
                        className="mr-2"
                      />
                      <label htmlFor="businessRegEnabled" className="font-medium">Business Registration Number</label>
                    </div>
                    {settings.invoiceCompliance?.businessRegistration?.enabled && (
                      <div className="ml-6">
                        <input
                          type="text"
                          value={settings.invoiceCompliance?.businessRegistration?.number || ""}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            invoiceCompliance: {
                              ...prev.invoiceCompliance,
                              businessRegistration: { ...prev.invoiceCompliance?.businessRegistration, number: e.target.value }
                            }
                          }))}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="Enter registration number"
                        />
                      </div>
                    )}
                  </div>

                  {/* VAT/Tax ID */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="vatTaxEnabled"
                        checked={settings.invoiceCompliance?.vatTaxId?.enabled || false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          invoiceCompliance: {
                            ...prev.invoiceCompliance,
                            vatTaxId: { ...prev.invoiceCompliance?.vatTaxId, enabled: e.target.checked }
                          }
                        }))}
                        className="mr-2"
                      />
                      <label htmlFor="vatTaxEnabled" className="font-medium">VAT/Tax ID</label>
                    </div>
                    {settings.invoiceCompliance?.vatTaxId?.enabled && (
                      <div className="ml-6">
                        <input
                          type="text"
                          value={settings.invoiceCompliance?.vatTaxId?.number || ""}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            invoiceCompliance: {
                              ...prev.invoiceCompliance,
                              vatTaxId: { ...prev.invoiceCompliance?.vatTaxId, number: e.target.value }
                            }
                          }))}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="Enter VAT/Tax ID"
                        />
                      </div>
                    )}
                  </div>

                  {/* Contact & Dispute Resolution */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="contactDisputeEnabled"
                        checked={settings.invoiceCompliance?.contactDispute?.enabled || false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          invoiceCompliance: {
                            ...prev.invoiceCompliance,
                            contactDispute: { ...prev.invoiceCompliance?.contactDispute, enabled: e.target.checked }
                          }
                        }))}
                        className="mr-2"
                      />
                      <label htmlFor="contactDisputeEnabled" className="font-medium">Contact & Dispute Resolution</label>
                    </div>
                    {settings.invoiceCompliance?.contactDispute?.enabled && (
                      <div className="ml-6 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Contact Email</label>
                          <input
                            type="email"
                            value={settings.invoiceCompliance?.contactDispute?.email || ""}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              invoiceCompliance: {
                                ...prev.invoiceCompliance,
                                contactDispute: { ...prev.invoiceCompliance?.contactDispute, email: e.target.value }
                              }
                            }))}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="contact@clinic.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Contact Phone</label>
                          <input
                            type="tel"
                            value={settings.invoiceCompliance?.contactDispute?.phone || ""}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              invoiceCompliance: {
                                ...prev.invoiceCompliance,
                                contactDispute: { ...prev.invoiceCompliance?.contactDispute, phone: e.target.value }
                              }
                            }))}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="+1 234 567 8900"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment Methods */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="paymentMethodsEnabled"
                        checked={settings.invoiceCompliance?.paymentMethods?.enabled || false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          invoiceCompliance: {
                            ...prev.invoiceCompliance,
                            paymentMethods: { ...prev.invoiceCompliance?.paymentMethods, enabled: e.target.checked }
                          }
                        }))}
                        className="mr-2"
                      />
                      <label htmlFor="paymentMethodsEnabled" className="font-medium">Payment Methods</label>
                    </div>
                    {settings.invoiceCompliance?.paymentMethods?.enabled && (
                      <div className="ml-6">
                        <input
                          type="text"
                          value={settings.invoiceCompliance?.paymentMethods?.methods || ""}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            invoiceCompliance: {
                              ...prev.invoiceCompliance,
                              paymentMethods: { ...prev.invoiceCompliance?.paymentMethods, methods: e.target.value }
                            }
                          }))}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="Bank transfer, Card payments, Cash"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Leave Management Settings Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave Management Settings</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Leave Year Start Date */}
                  <div>
                    <h4 className="font-semibold mb-3">Leave Year Start Date</h4>
                    <div className="flex items-center space-x-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Month</label>
                        <select
                          value={settings.leaveSettings?.yearStartMonth || 1}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              yearStartMonth: parseInt(e.target.value)
                            }
                          }))}
                          className="border rounded px-2 py-1"
                        >
                          <option value={1}>January</option>
                          <option value={2}>February</option>
                          <option value={3}>March</option>
                          <option value={4}>April</option>
                          <option value={5}>May</option>
                          <option value={6}>June</option>
                          <option value={7}>July</option>
                          <option value={8}>August</option>
                          <option value={9}>September</option>
                          <option value={10}>October</option>
                          <option value={11}>November</option>
                          <option value={12}>December</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Day</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={settings.leaveSettings?.yearStartDay || 1}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              yearStartDay: parseInt(e.target.value) || 1
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      Leave balances will reset on this date each year
                    </div>
                  </div>

                  {/* Auto Rollover */}
                  <div>
                    <h4 className="font-semibold mb-3">Leave Balance Rollover</h4>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={settings.leaveSettings?.autoRollover !== false}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          leaveSettings: {
                            ...prev.leaveSettings,
                            autoRollover: e.target.checked
                          }
                        }))}
                        className="rounded"
                      />
                      <span className="text-sm">Automatically rollover unused leave to next year</span>
                    </label>
                    <div className="text-xs text-gray-500 mt-2">
                      When enabled, unused annual leave will carry over to the next leave year
                    </div>
                  </div>
                </div>

                {/* Leave Allowances */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Annual Leave Allowances</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Annual Leave</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={settings.leaveSettings?.annualLeaveAllowance || 25}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              annualLeaveAllowance: parseInt(e.target.value) || 25
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">days/year</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Sick Leave</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={settings.leaveSettings?.sickLeaveAllowance || 10}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              sickLeaveAllowance: parseInt(e.target.value) || 10
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">days/year</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Personal Leave</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={settings.leaveSettings?.personalLeaveAllowance || 5}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              personalLeaveAllowance: parseInt(e.target.value) || 5
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">days/year</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Emergency Leave</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={settings.leaveSettings?.emergencyLeaveAllowance || 3}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              emergencyLeaveAllowance: parseInt(e.target.value) || 3
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">days/year</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Maternity/Paternity Leave</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={settings.leaveSettings?.maternityLeaveAllowance || 90}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            leaveSettings: {
                              ...prev.leaveSettings,
                              maternityLeaveAllowance: parseInt(e.target.value) || 90
                            }
                          }))}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-sm text-gray-600">days/year</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Set the annual allowance for each leave type. These will be used to calculate leave balances for all staff members.
                  </div>
                </div>

                {/* Current Leave Year Display */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="text-sm font-medium text-blue-900">
                    Current Leave Year: {getCurrentLeaveYear(settings.leaveSettings)}
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Settings"}
                </button>
              </div>
              {message && <div className="text-green-600 mt-4">{message}</div>}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
